<template>
  <div v-if="!isPreviewError" class="feed-tagging-container">
    <div v-if="!isPreviewLoading">
      <div class="dashboard-content">
        <div class="mb-4 pb-3">
          <p class="mb-0">{{$t('feeds.tagging_description')}}</p>
        </div>
        <div class="my-4 pb-4">
          <b-form-checkbox id="addTaggingCheckbox" v-model="addTagging" @change="setTagging"><strong>{{$t('feeds.add_tagging')}}</strong></b-form-checkbox>
        </div>
        <transition name="fade" mode="out-in">
          <div v-if="addTagging">
            <div v-for="(taggingRule, index) in taggingRules" v-bind:key="index+1" class="d-flex align-items-center mt-4 pt-3">
              <div class="tagging-rule-column1">
                <p class="mb-1"><strong>{{ taggingRule.label }}</strong></p>
                <h6 v-if="index < 3" class="mb-0 text-danger"><b-badge variant="danger">{{ $t('global.required') }}</b-badge></h6>
                <h6 v-else class="text-info mb-0"><b-badge variant="info">{{ $t('global.optional') }}</b-badge></h6>
              </div>
              <div class="tagging-rule-column2 mr-4">
                <input v-model="taggingRule.value" class="form-control w-100" :name="'taggingRule'+index" type="text" :disabled="taggingRule.isDynamic">
              </div>
              <div class="tagging-rule-column3 ml-4" v-if="taggingRule.tag === 'content' || taggingRule.tag === 'term'">
                <b-form-checkbox @change="onDynamicFieldSelect(taggingRule)" :id="'isDynamic'+index" v-model="taggingRule.isDynamic">{{$t('feeds.use_dynamic')}}</b-form-checkbox>
              </div>
              <div class="tagging-rule-column4 mx-4" v-if="taggingRule.tag === 'content' || taggingRule.tag === 'term'">
                <multiselect
                  @select="onFilteringFieldSelect($event, taggingRule)"
                  label="label"
                  v-model="taggingRule.filteringField"
                  v-show="taggingRule.isDynamic"
                  :allow-empty="false"
                  :deselect-label="''"
                  :options="filteringFields"
                  :placeholder="$t('feeds.choose_variable')"
                  :searchable="true"
                  :select-label="''"
                  :selected-label="''">
                  <template slot="option" slot-scope="props">
                    <span v-if="props.option.type === 'field'" class="option__title d-flex align-items-center justify-content-between bg-orange-light w-100 py-0 pl-4">{{ props.option.label }}</span>
                    <span v-if="props.option.type === 'source'" class="option__title d-flex align-items-center justify-content-between bg-purple w-100 py-0 pl-4">{{ props.option.label }}</span>
                  </template>
                  <span slot="noResult">{{ $t('global.multiselect_search_not_found') }}</span>
                </multiselect>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <div v-if="$route.params.feedId !== undefined" class="d-flex justify-content-between">
        <router-link :to="{ name: 'feed.edit.filters', params: { id: $route.params.projectId, feedId: feed.id, feed: feed }}">
          <button class="btn btn-large btn-gray-dark">
            <arrow-left-circle-icon></arrow-left-circle-icon>
            <span>{{ $t('global.back') }}</span>
          </button>
        </router-link>
        <button @click="submitFeedConfig" :disabled="!isValid" class="btn btn-large btn-orange-dark">
          <eye-icon></eye-icon>
          <span>{{ $t('global.preview') }}</span>
        </button>
      </div>
      <div v-else class="d-flex justify-content-between">
        <router-link :to="{ name: 'feed.create.filters', params: { id: $route.params.projectId, feed: feed }}">
          <button class="btn btn-large btn-gray-dark">
            <arrow-left-circle-icon></arrow-left-circle-icon>
            <span>{{ $t('global.back') }}</span>
          </button>
        </router-link>
        <button @click="submitFeedConfig" :disabled="!isValid" class="btn btn-large btn-orange-dark">
          <eye-icon></eye-icon>
          <span>{{ $t('global.preview') }}</span>
        </button>
      </div>
    </div>
    <div v-else class="d-flex justify-content-center align-items-center">
      <div class="spinner-border text-orange" role="status"></div>
      <div class="mt-5 d-flex flex-column align-items-center">
        <h5>{{$t('feeds.generating')}}</h5>
      </div>
    </div>
  </div>
  <div v-else>
  <div class="alert alert-danger mb-4 mt-3" role="alert">
    {{$t('feeds.generating_error')}}
  </div>
  <div class="d-flex" v-if="$route.params.feedId !== undefined">
    <router-link :to="{ name: 'feed.edit.field-mapping', params: { projectId: this.$route.params.projectId, feedId: feed.id, feed: feed }}" class="mr-2">
      <button type="button" class="btn btn-large btn-orange-dark">
        <plus-circle-icon></plus-circle-icon>
        <span>{{$t('global.try_again')}}</span>
      </button>
    </router-link>
  </div>
  <div class="d-flex" v-else>
    <router-link :to="{ name: 'feed.create.main-settings', params: { projectId: this.$route.params.projectId, feed: { name: '' } }}" class="mr-2">
      <button type="button" class="btn btn-large btn-orange-dark">
        <plus-circle-icon></plus-circle-icon>
        <span>{{$t('global.try_again')}}</span>
      </button>
    </router-link>
  </div>
</div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { ArrowLeftCircleIcon, EyeIcon, PlusCircleIcon } from 'vue-feather-icons'
import axios from 'axios'
import feeds from '@/mixins/feeds'

export default {
  name: 'Tagging',

  data () {
    return {
      addTagging: false,
      feedResultType: '',
      isPreviewError: false,
      isPreviewLoading: false,
      isValid: true,
      taggingRules: this.feed.config.taggingRules
    }
  },

  props: {
    feed: { required: true, type: Object }
  },

  mixins: [feeds],

  created () {
    if (this.taggingRules === undefined) this.taggingRules = []
    this.initTaggingRules(this.feed.config.mappingRules, this.feed.feedType)
    if (this.taggingRules.length > 0) this.addTagging = true
    if (this.feed.config.resultType === undefined) this.feedResultType = this.feed.type.resultType
    else this.feedResultType = this.feed.config.resultType
  },

  methods: {
    setTagging (value) {
      if (!value) {
        this.taggingRules.splice(4, 1)
        this.taggingRules.splice(3, 1)
        this.taggingRules.splice(2, 1)
        this.taggingRules.splice(1, 1)
        this.taggingRules.splice(0, 1)
      } else {
        this.taggingRules.push({ label: this.$i18n.t('global.campaign_source'), tag: 'source', value: '', isDynamic: false })
        this.taggingRules.push({ label: this.$i18n.t('global.campaign_type'), tag: 'medium', value: '', isDynamic: false })
        this.taggingRules.push({ label: this.$i18n.t('global.campaign_name'), tag: 'campaign', value: '', isDynamic: false })
        this.taggingRules.push({ label: this.$i18n.t('global.content'), tag: 'content', value: '', isDynamic: false })
        this.taggingRules.push({ label: this.$i18n.t('global.keyword'), tag: 'term', value: '', isDynamic: false })
      }
    },

    onDynamicFieldSelect (taggingRule) {
      taggingRule.value = ''
      taggingRule.filteringField = null
    },

    onFilteringFieldSelect (filteringField, taggingRule) {
      taggingRule.value = filteringField.value
    },

    submitFeedConfig () {
      this.isPreviewLoading = true
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/feed/preview/' + this.$route.params.projectId, this.finalFeedConfig)
        .then((response) => {
          this.isLoading = false
          if ((response.status === 201) || (response.status === 200)) {
            const feedData = response.data
            this.isPreviewError = false
            this.isPreviewLoading = false
            if (this.$route.params.feedId !== undefined) this.$router.push({ name: 'feed.edit.preview', params: { projectId: this.$route.params.projectId, feedId: this.feed.id, feed: this.feed, feedData: feedData } })
            else this.$router.push({ name: 'feed.create.preview', params: { projectId: this.$route.params.projectId, feed: this.feed, feedData: feedData } })
          }
        })
        .catch(() => {
          this.isPreviewError = true
        })
    }
  },

  watch: {
    taggingRules: {
      handler (newVal) {
        if (this.addTagging) {
          if ((newVal[0].value === '') || (newVal[1].value === '') || (newVal[2].value === '')) this.isValid = false
          else if ((newVal[3].isDynamic) && (newVal[3].value === '')) this.isValid = false
          else if ((newVal[4].isDynamic) && (newVal[4].value === '')) this.isValid = false
          else this.isValid = true
        } else this.isValid = true
      },
      deep: true
    }
  },

  computed: {
    finalFeedConfig () {
      return {
        name: this.feed.name,
        active: this.feed.active,
        config: {
          resultType: this.feedResultType,
          formatRules: this.feed.config.formatRules.map(function (formatRule) {
            return {
              conditions: formatRule.conditions.map(function (condition) {
                return {
                  columnName: condition.columnName,
                  conditionType: condition.conditionType,
                  conditionString: condition.conditionString
                }
              }),
              columnName: formatRule.columnName,
              operationType: formatRule.operationType,
              toReplace: formatRule.toReplace,
              replaceWith: formatRule.replaceWith
            }
          }),
          mappingRules: this.feed.config.mappingRules.map(function (mappingRule) {
            return { from: mappingRule.from, to: mappingRule.to }
          }),
          filterRules: this.feed.config.filterRules.map(function (filterRule) {
            return {
              conditions: filterRule.conditions.map(function (condition) {
                let columnName = ''
                if (condition.filteringField !== null && condition.filteringField !== undefined) columnName = condition.filteringField.value
                let conditionType = ''
                if (condition.filteringType !== null && condition.filteringType !== undefined) conditionType = condition.filteringType.value
                return {
                  columnName: columnName,
                  conditionType: conditionType,
                  conditionString: condition.conditionString
                }
              })
            }
          }),
          taggingRules: this.taggingRules.map(function (taggingRule) {
            return { tag: taggingRule.tag, value: taggingRule.value, isDynamic: taggingRule.isDynamic }
          })
        },
        feedType: this.feed.feedType
      }
    }
  },

  components: { ArrowLeftCircleIcon, EyeIcon, Multiselect, PlusCircleIcon }
}
</script>
